import React from 'react';
import { PermissionsState } from '../hooks/usePermissions';
import { ModulesPermissions, ModulesPermissionsFlags } from './ModulesPermissionsSection';
import { useSelector } from 'hooks/utils';
import { LanguageType } from 'types/index';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';

const DICTIONARY = {
	[LanguageType.English]: {
		reviewAndConfirm: 'Review & Confirm',
		selectedUsers: 'Selected Users',
		noUsersSelected: 'No users selected',
		permissionsSummary: 'Permissions Summary',
		accessLevel: 'Access Level',
		moduleAccess: 'Module Access',
		noModuleAccess: 'No module access granted',
		groups: 'Groups',
		dataPermissionDetails: 'Data Permission Details',
		ownData: 'Own Data',
		groupData: 'Group Data',
		allData: 'All Data',
		additional: 'Additional',
		view: 'View',
		edit: 'Edit',
		delete: 'Delete',
		analyze: 'Analyze',
		exportData: 'Export data',
		accessRestricted: 'Access restricted variables',
		editVariables: 'Edit variables',
		editProjectInfo: 'Edit project info',
		specificPermissions: 'specific permissions enabled',
		fullAccess: 'Full project access',
		groupAccess: 'Group-level access',
		ownAccess: 'Own data access only',
		minimalAccess: 'Minimal access',
		projectDesign: 'Project Design',
		collaborators: 'Collaborators',
		allowCodelistRead: 'Allow reading of codelists',
		allowCodelistWrite: 'Allow writing to codelists',
		allowCodelistExport: 'Allow exporting codelists'
	},
	[LanguageType.Norwegian]: {
		reviewAndConfirm: 'Gjennomgå og bekreft',
		selectedUsers: 'Valgte brukere',
		noUsersSelected: 'Ingen brukere valgt',
		permissionsSummary: 'Tillatelsessammendrag',
		accessLevel: 'Tilgangsnivå',
		moduleAccess: 'Modultilgang',
		noModuleAccess: 'Ingen modultilgang gitt',
		groups: 'Grupper',
		dataPermissionDetails: 'Datatillatelsesdetaljer',
		ownData: 'Egne data',
		groupData: 'Gruppedata',
		allData: 'Alle data',
		additional: 'Ytterligere',
		view: 'Vis',
		edit: 'Rediger',
		delete: 'Slett',
		analyze: 'Analyser',
		exportData: 'Eksporter data',
		accessRestricted: 'Tilgang til begrensede variabler',
		editVariables: 'Rediger variabler',
		editProjectInfo: 'Rediger prosjektinfo',
		specificPermissions: 'spesifikke tillatelser aktivert',
		fullAccess: 'Full prosjekttilgang',
		groupAccess: 'Gruppenivåtilgang',
		ownAccess: 'Kun tilgang til egne data',
		minimalAccess: 'Minimal tilgang',
		projectDesign: 'Prosjektdesign',
		collaborators: 'Samarbeidspartnere',
		allowCodelistRead: 'Tillat lesing av kodelister',
		allowCodelistWrite: 'Tillat skriving til kodelister',
		allowCodelistExport: 'Tillat eksport av kodelister'
	}
};

export interface ConfirmStepProps {
	confirmedUsers: string[];
	permissions: PermissionsState;
	modules: ModulesPermissions;
	selectedGroups: string[];
}

export const ConfirmStep: React.FC<ConfirmStepProps> = ({
	confirmedUsers,
	permissions,
	modules,
	selectedGroups
}) => {
	const isCodelistEnabledForProject = useIsCodelistEnabledForProject();
	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];

	const countEnabledPermissions = (permissions: PermissionsState): number => {
		return Object.values(permissions).filter(value => value === true).length;
	};

	const countEnabledModules = (modules: ModulesPermissions): number => {
		return Object.values(modules).filter(value => value === true).length;
	};

	const getEnabledModuleNames = (): string[] => {
		const moduleNames: string[] = [];

		if (modules[ModulesPermissionsFlags.projectDesign]) {
			moduleNames.push(dictionary.projectDesign);
		}

		if (modules[ModulesPermissionsFlags.collaborators]) {
			moduleNames.push(dictionary.collaborators);
		}

		return moduleNames;
	};

	const getPermissionLevelDescription = (): string => {
		if (permissions.accessEntriesAllRead) {
			return dictionary.fullAccess;
		} else if (permissions.accessEntriesOrgRead) {
			return dictionary.groupAccess;
		} else if (permissions.accessEntriesOwnonlyRead) {
			return dictionary.ownAccess;
		} else {
			return dictionary.minimalAccess;
		}
	};

	return (
		<div>
			<h2 className="text-2xl font-bold text-gray-800 mb-6">{dictionary.reviewAndConfirm}</h2>

			{/* Users Section */}
			<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
				<h3 className="text-xl font-semibold text-gray-800 mb-4">
					{dictionary.selectedUsers} ({confirmedUsers.length})
				</h3>

				{confirmedUsers.length === 0 ? (
					<div className="text-gray-500">{dictionary.noUsersSelected}</div>
				) : (
					<div className="space-y-2">
						{confirmedUsers.map((user, index) => (
							<div
								key={index}
								className="p-3 bg-gray-50 rounded-lg flex items-center"
							>
								<div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mr-3">
									{user.charAt(0).toUpperCase()}
								</div>
								<div>
									<span className="text-base text-gray-700">{user}</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Permissions Summary */}
			<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
				<h3 className="text-xl font-semibold text-gray-800 mb-4">
					{dictionary.permissionsSummary}
				</h3>

				<div className="space-y-4">
					<div className="p-4 bg-gray-50 rounded-lg">
						<span className="text-lg font-medium text-gray-700 block mb-1">
							{dictionary.accessLevel}:
						</span>
						<span className="text-base text-gray-600">
							{getPermissionLevelDescription()}
						</span>
						<div className="mt-2 text-sm text-gray-500">
							{countEnabledPermissions(permissions)} {dictionary.specificPermissions}
						</div>
					</div>

					{/* Module Access */}
					<div className="p-4 bg-gray-50 rounded-lg">
						<span className="text-lg font-medium text-gray-700 block mb-1">
							{dictionary.moduleAccess}:
						</span>
						{countEnabledModules(modules) === 0 ? (
							<span className="text-sm text-gray-600">
								{dictionary.noModuleAccess}
							</span>
						) : (
							<div>
								{getEnabledModuleNames().map(moduleName => (
									<div key={moduleName} className="flex items-center mb-1">
										<div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
										<span className="text-sm text-gray-700">{moduleName}</span>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Groups */}
					{selectedGroups.length > 0 && (
						<div className="p-4 bg-gray-50 rounded-lg">
							<span className="text-lg font-medium text-gray-700 block mb-1">
								{dictionary.groups}:
							</span>
							<div className="space-y-2 mt-2">
								{selectedGroups.map((group, index) => (
									<div
										key={'selected_group_' + index}
										className="flex items-center"
									>
										<div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
										<span className="text-base text-gray-700">{group}</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Data Permissions Details */}
			<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
				<h3 className="text-xl font-semibold text-gray-800 mb-4">
					{dictionary.dataPermissionDetails}
				</h3>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="p-3 bg-gray-50 rounded-lg">
						<span className="text-base font-medium text-gray-700 block mb-2">
							{dictionary.ownData}:
						</span>
						<ul className="ml-4 space-y-1">
							{permissions.accessEntriesOwnonlyRead && (
								<li className="text-sm text-gray-600">{dictionary.view}</li>
							)}
							{permissions.accessEntriesOwnonlyWrite && (
								<li className="text-sm text-gray-600">{dictionary.edit}</li>
							)}
							{permissions.accessEntriesOwnonlyDelete && (
								<li className="text-sm text-gray-600">{dictionary.delete}</li>
							)}
							{permissions.accessAnalysisOwnonly && (
								<li className="text-sm text-gray-600">{dictionary.analyze}</li>
							)}
						</ul>
					</div>

					<div className="p-3 bg-gray-50 rounded-lg">
						<span className="text-base font-medium text-gray-700 block mb-2">
							{dictionary.groupData}:
						</span>
						<ul className="ml-4 space-y-1">
							{permissions.accessEntriesOrgRead && (
								<li className="text-sm text-gray-600">{dictionary.view}</li>
							)}
							{permissions.accessEntriesOrgWrite && (
								<li className="text-sm text-gray-600">{dictionary.edit}</li>
							)}
							{permissions.accessEntriesOrgDelete && (
								<li className="text-sm text-gray-600">{dictionary.delete}</li>
							)}
							{permissions.accessAnalysisOrg && (
								<li className="text-sm text-gray-600">{dictionary.analyze}</li>
							)}
						</ul>
					</div>

					<div className="p-3 bg-gray-50 rounded-lg">
						<span className="text-base font-medium text-gray-700 block mb-2">
							{dictionary.allData}:
						</span>
						<ul className="ml-4 space-y-1">
							{permissions.accessEntriesAllRead && (
								<li className="text-sm text-gray-600">{dictionary.view}</li>
							)}
							{permissions.accessEntriesAllWrite && (
								<li className="text-sm text-gray-600">{dictionary.edit}</li>
							)}
							{permissions.accessEntriesAllDelete && (
								<li className="text-sm text-gray-600">{dictionary.delete}</li>
							)}
							{permissions.accessAnalysisAll && (
								<li className="text-sm text-gray-600">{dictionary.analyze}</li>
							)}
						</ul>
					</div>

					<div className="p-3 bg-gray-50 rounded-lg">
						<span className="text-base font-medium text-gray-700 block mb-2">
							{dictionary.additional}:
						</span>
						<ul className="ml-4 space-y-1">
							{isCodelistEnabledForProject && permissions.accessCodelistRead && (
								<li className="text-sm text-gray-600">
									{dictionary.allowCodelistRead}
								</li>
							)}
							{isCodelistEnabledForProject && permissions.accessCodelistWrite && (
								<li className="text-sm text-gray-600">
									{dictionary.allowCodelistWrite}
								</li>
							)}
							{isCodelistEnabledForProject && permissions.accessCodelistExport && (
								<li className="text-sm text-gray-600">
									{dictionary.allowCodelistExport}
								</li>
							)}
							{permissions.accessExport && (
								<li className="text-sm text-gray-600">{dictionary.exportData}</li>
							)}
							{permissions.accessPersonalData && (
								<li className="text-sm text-gray-600">
									{dictionary.accessRestricted}
								</li>
							)}
							{permissions.accessVariablesWrite && (
								<li className="text-sm text-gray-600">
									{dictionary.editVariables}
								</li>
							)}
							{permissions.accessProjectWrite && (
								<li className="text-sm text-gray-600">
									{dictionary.editProjectInfo}
								</li>
							)}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
};
